import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import sharepointConfig from '../config/sharepointConfig';
import { fmtDatum, berekenUrenPeriode, berekenVerlofUrenHeleDag } from '../utils/datum';

function StatCard({ icon, label, value, subtitle, color, onClick }) {
  return (
    <div style={{ ...styles.statCard, cursor: onClick ? 'pointer' : 'default' }} onClick={onClick}>
      <div style={{ ...styles.statIcon, background: `${color}15`, color }}>{icon}</div>
      <div>
        <div style={styles.statValue} className="stat-val">{value}</div>
        <div style={styles.statLabel}>{label}</div>
        {subtitle && <div style={styles.statSubtitle}>{subtitle}</div>}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const config = sharepointConfig.statusOpties.find((s) => s.key === status);
  return (
    <span style={{ ...styles.badge, background: `${config?.color || '#767676'}15`, color: config?.color || '#767676' }}>
      {config?.text || status}
    </span>
  );
}

function AdminDashboard({ navigate, currentUser, openAanvragen, medewerkersExAdmin, recentAanvragen, verlofaanvragen, overuren, medewerkers, verlofTypes, indienVerlofaanvraag, feestdagen, updateVerlofStatus, vakantiePerioden }) {
  const [pMwId, setPMwId] = useState('');
  const [pForm, setPForm] = useState({ Type: '', StartDatum: '', EindDatum: '', Uren: '', Reden: '', isDagdeel: false, StartTijd: '', EindTijd: '', directGoedkeuren: true });
  const [pSubmitting, setPSubmitting] = useState(false);
  const [pSuccess, setPSuccess] = useState('');

  const [processingId, setProcessingId] = useState(null);

  const handleStatusUpdate = async (id, status) => {
    setProcessingId(id);
    try { await updateVerlofStatus(id, status); } catch (err) { console.error(err); }
    finally { setProcessingId(null); }
  };

  const pMw = medewerkers.find(m => m.Id === pMwId);

  const handlePortaalSubmit = async () => {
    const eindDatum = pForm.EindDatum || pForm.StartDatum;
    const urenStr = String(pForm.Uren).replace(',', '.');
    const uren = Number(urenStr) || 0;
    if (!pMwId || !pForm.Type || !pForm.StartDatum || uren <= 0) return;
    setPSubmitting(true);
    try {
      const vt = (verlofTypes || []).find(t => t.key === pForm.Type);
      await indienVerlofaanvraag({
        medewerkerId: pMw.Id, medewerkerNaam: pMw.Naam,
        Type: pForm.Type, StartDatum: pForm.StartDatum,
        EindDatum: eindDatum,
        StartTijd: pForm.isDagdeel ? pForm.StartTijd : null,
        EindTijd: pForm.isDagdeel ? pForm.EindTijd : null,
        Uren: uren,
        Reden: pForm.Reden || '',
        directGoedkeuren: pForm.directGoedkeuren,
      });
      setPSuccess(`${vt?.text || pForm.Type} voor ${pMw.Naam} ${pForm.directGoedkeuren ? 'goedgekeurd' : 'ingediend'}`);
      setPForm({ Type: '', StartDatum: '', EindDatum: '', Uren: '', Reden: '', isDagdeel: false, StartTijd: '', EindTijd: '', directGoedkeuren: true });
    } catch (err) { console.error(err); } finally { setPSubmitting(false); }
  };

  return (
    <div style={styles.container}>
      {/* Outlook verbinding banner — alleen tonen als niet verbonden op dit apparaat */}
      {(() => {
        const connected = localStorage.getItem('slaaploods_outlook_connected');
        const azureConfig = localStorage.getItem('slaaploods_azure_config');
        if (azureConfig && connected === '0') {
          return (
            <div style={{ background: '#fff4ce', border: '1px solid #FFB900', borderRadius: 10, padding: '10px 16px', marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', fontSize: 13 }}>
              <div>
                <span style={{ fontWeight: 700, color: '#7A6400' }}>📅 Outlook agenda niet gekoppeld op dit apparaat</span>
                <span style={{ color: '#605e5c', marginLeft: 8 }}>Koppel eenmalig voor automatische agenda-sync.</span>
              </div>
              <button onClick={() => navigate('/admin', { state: { tab: 'agenda' } })}
                style={{ padding: '5px 14px', borderRadius: 6, border: 'none', background: '#0078D4', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                Koppelen →
              </button>
            </div>
          );
        }
        return null;
      })()}
      <div style={styles.welcomeCard} className="r-welcome">
        <div>
          <h2 style={styles.welcomeTitle}>Beheerder dashboard 👑</h2>
          <p style={styles.welcomeText}>
            {openAanvragen.length > 0
              ? `Er zijn ${openAanvragen.length} openstaande verlofaanvragen om te beoordelen.`
              : 'Alle aanvragen zijn afgehandeld.'}
          </p>
        </div>
        <button style={styles.ctaButton} onClick={() => navigate('/admin')}>Beheer →</button>
      </div>
      <div style={styles.statsGrid} className="r-stats">
        <StatCard icon="📝" label="Openstaand" value={openAanvragen.length}
          subtitle="aanvragen te beoordelen" color="#FFB900"
          onClick={() => navigate('/verlof-overzicht')} />
        <StatCard icon="✅" label="Goedgekeurd" value={verlofaanvragen.filter(v => v.Status === 'goedgekeurd').length}
          subtitle="dit jaar" color="#107C10" />
        <StatCard icon="👥" label="Medewerkers" value={medewerkersExAdmin.length}
          subtitle="actief" color="#0078D4" onClick={() => navigate('/admin')} />
        <StatCard icon="⏰" label="Open overuren" value={overuren.filter(o => o.Status === 'ingediend').length}
          subtitle="te beoordelen" color="#E86E1C" onClick={() => navigate('/overuren')} />
      </div>

      {/* ===== SNEL VERLOF INDIENEN ===== */}
      <div style={{ ...styles.card, border: '2px solid #E86E1C', borderRadius: 14 }} className="r-card">
        <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1a1a2e', marginBottom: 14 }}>📝 Snel verlof indienen</h3>
        {pSuccess && <div style={{ background: '#dff6dd', border: '1px solid #a7e3a5', borderRadius: 8, padding: '8px 12px', marginBottom: 12, color: '#107C10', fontSize: 13 }}>✅ {pSuccess}</div>}
        <div className="r-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
          <div>
            <label style={styles.miniLabel}>Medewerker</label>
            <select style={styles.miniInput} value={pMwId} onChange={e => { setPMwId(e.target.value); setPSuccess(''); }}>
              <option value="" disabled>--- Kies ---</option>
              {medewerkers.filter(m => (m.Status || 'actief') === 'actief').map(m => (
                <option key={m.Id} value={m.Id}>{m.Naam}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={styles.miniLabel}>Type verlof</label>
            <select style={styles.miniInput} value={pForm.Type} onChange={e => setPForm({...pForm, Type: e.target.value})}>
              <option value="" disabled>--- Kies ---</option>
              {(verlofTypes || []).filter(t => t.key !== 'ziek' && t.key !== 'kort-verzuim' && t.key !== 'flex-plus').map(t => <option key={t.key} value={t.key}>{t.text}{t.aftrekSaldo === 'geen' ? ' (geen aftrek)' : ''}</option>)}
            </select>
          </div>
          <div style={{ gridColumn: 'span 2', marginTop: -4 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, cursor: 'pointer', color: '#605e5c' }}>
                  <input type="checkbox" checked={pForm.isDagdeel} onChange={e => {
                    const isDagdeel = e.target.checked;
                    setPForm(f => ({...f, isDagdeel, StartTijd: isDagdeel ? f.StartTijd : '', EindTijd: isDagdeel ? f.EindTijd : '', Uren: isDagdeel ? '' : f.Uren}));
                  }} />
                  🕐 Met start-/eindtijd
                </label>
              </div>
              <div>
                <label style={styles.miniLabel}>Startdatum</label>
                <input type="date" style={styles.miniInput} value={pForm.StartDatum} onChange={e => {
              const sd = e.target.value;
              try {
                if (pForm.isDagdeel && pForm.StartTijd && pForm.EindTijd) {
                  const ed = pForm.EindDatum || sd;
                  const info = berekenUrenPeriode(sd, pForm.StartTijd, ed, pForm.EindTijd, (feestdagen||[]).map(f=>f.Datum), pMw?.Werkrooster);
                  setPForm(f => ({...f, StartDatum: sd, Uren: info.netto > 0 ? String(info.netto) : f.Uren}));
                } else if (!pForm.isDagdeel && pForm.EindDatum && pMw) {
                  const hd = berekenVerlofUrenHeleDag(sd, pForm.EindDatum, (feestdagen||[]).map(f=>f.Datum), pMw?.Werkrooster);
                  setPForm(f => ({...f, StartDatum: sd, Uren: hd.uren > 0 ? String(hd.uren) : f.Uren}));
                } else {
                  setPForm(f => ({...f, StartDatum: sd}));
                }
              } catch (err) { console.error('Uren berekening mislukt:', err); setPForm(f => ({...f, StartDatum: sd})); }
            }} />
              </div>
              {pForm.isDagdeel ? (
                <>
                  <div>
                    <label style={styles.miniLabel}>Starttijd</label>
                    <input type="time" style={styles.miniInput} value={pForm.StartTijd} onChange={e => {
                      const st = e.target.value;
                      try {
                        const ed = pForm.EindDatum || pForm.StartDatum;
                        const info = berekenUrenPeriode(pForm.StartDatum, st, ed, pForm.EindTijd, (feestdagen||[]).map(f=>f.Datum), pMw?.Werkrooster);
                        setPForm(f => ({...f, StartTijd: st, Uren: info.netto > 0 ? String(info.netto) : ''}));
                      } catch (err) { console.error('Uren berekening mislukt:', err); setPForm(f => ({...f, StartTijd: st})); }
                    }} />
                  </div>
                  <div>
                    <label style={styles.miniLabel}>Einddatum</label>
                    <input type="date" style={styles.miniInput} value={pForm.EindDatum || pForm.StartDatum} onChange={e => {
                      const ed = e.target.value;
                      try {
                        const info = berekenUrenPeriode(pForm.StartDatum, pForm.StartTijd, ed, pForm.EindTijd, (feestdagen||[]).map(f=>f.Datum), pMw?.Werkrooster);
                        setPForm(f => ({...f, EindDatum: ed, Uren: info.netto > 0 ? String(info.netto) : f.Uren}));
                      } catch (err) { console.error('Uren berekening mislukt:', err); setPForm(f => ({...f, EindDatum: ed})); }
                    }} />
                  </div>
                  <div>
                    <label style={styles.miniLabel}>Eindtijd</label>
                    <input type="time" style={styles.miniInput} value={pForm.EindTijd} onChange={e => {
                      const et = e.target.value;
                      try {
                        const ed = pForm.EindDatum || pForm.StartDatum;
                        const info = berekenUrenPeriode(pForm.StartDatum, pForm.StartTijd, ed, et, (feestdagen||[]).map(f=>f.Datum), pMw?.Werkrooster);
                        setPForm(f => ({...f, EindTijd: et, Uren: info.netto > 0 ? String(info.netto) : ''}));
                      } catch (err) { console.error('Uren berekening mislukt:', err); setPForm(f => ({...f, EindTijd: et})); }
                    }} />
                  </div>
                  <div style={{ gridColumn: 'span 2' }}>
                    <label style={styles.miniLabel}>Uren berekening</label>
                    <input type="text" inputMode="decimal" style={styles.miniInput} value={pForm.Uren} onChange={e => setPForm({...pForm, Uren: e.target.value})} placeholder="bv. 2,5" />
                  </div>
                  {pForm.StartDatum && pForm.StartTijd && pForm.EindTijd && (() => {
                    try {
                    const ed = pForm.EindDatum || pForm.StartDatum;
                    const info = berekenUrenPeriode(pForm.StartDatum, pForm.StartTijd, ed, pForm.EindTijd, (feestdagen||[]).map(f=>f.Datum), pMw?.Werkrooster);
                    if (info.netto <= 0) return null;
                    return (
                      <div style={{ gridColumn: 'span 2', fontSize: 11, color: '#605e5c', background: '#f0f6ff', padding: '6px 10px', borderRadius: 6, lineHeight: 1.5 }}>
                        ⏱️ <strong>{info.netto}u</strong>{info.werkdagen > 1 ? ` (${info.werkdagen} werkdagen)` : ''}
                        {info.details.length > 1 && info.details.filter(d=>d.uren>0).map((d,i) => (
                          <span key={i} style={{ display: 'block', fontSize: 10, color: '#8a8886' }}>
                            {fmtDatum(d.datum)}: {d.type === 'hele dag' ? `${d.uren}u` : `${d.van}–${d.tot} = ${d.uren}u`}
                          </span>
                        ))}
                      </div>
                    );
                    } catch (err) { console.error('Preview berekening mislukt:', err); return null; }
                  })()}
                </>
              ) : (
                <>
                  <div>
                    <label style={styles.miniLabel}>Einddatum</label>
                    <input type="date" style={styles.miniInput} value={pForm.EindDatum} onChange={e => {
                      const ed = e.target.value;
                      try {
                        if (pForm.StartDatum && ed && pMw) {
                          const hd = berekenVerlofUrenHeleDag(pForm.StartDatum, ed, (feestdagen||[]).map(f=>f.Datum), pMw?.Werkrooster);
                          setPForm(f => ({...f, EindDatum: ed, Uren: hd.uren > 0 ? String(hd.uren) : f.Uren}));
                        } else {
                          setPForm(f => ({...f, EindDatum: ed}));
                        }
                      } catch (err) { console.error('Uren berekening mislukt:', err); setPForm(f => ({...f, EindDatum: ed})); }
                    }} />
                  </div>
                  <div>
                    <label style={styles.miniLabel}>Uren berekening</label>
                    <input type="text" inputMode="decimal" style={styles.miniInput} value={pForm.Uren} onChange={e => setPForm({...pForm, Uren: e.target.value})} placeholder="bv. 8" />
                    {pForm.StartDatum && pForm.EindDatum && pMw && (() => {
                      const hd = berekenVerlofUrenHeleDag(pForm.StartDatum, pForm.EindDatum, (feestdagen||[]).map(f=>f.Datum), pMw?.Werkrooster);
                      return hd.werkdagen > 0 ? <div style={{ fontSize: 10, color: '#605e5c', marginTop: 2 }}>Rooster: {hd.werkdagen} werkdag{hd.werkdagen > 1 ? 'en' : ''} = {hd.uren}u</div> : null;
                    })()}
                  </div>
                </>
              )}
              <div style={{ gridColumn: 'span 2' }}>
                <label style={styles.miniLabel}>Opmerkingen (optioneel)</label>
                <input style={styles.miniInput} value={pForm.Reden} onChange={e => setPForm({...pForm, Reden: e.target.value})} placeholder="Bijv. familiebezoek..." />
              </div>
        </div>
        {/* Vakantieperiode waarschuwing */}
        {(() => {
          if (!pForm.StartDatum || !vakantiePerioden || vakantiePerioden.length === 0) return null;
          const sd = pForm.StartDatum;
          const ed = pForm.EindDatum || pForm.StartDatum;
          const overlapping = vakantiePerioden.filter(vp => sd <= vp.eindDatum && ed >= vp.startDatum);
          if (overlapping.length === 0) return null;
          return (
            <div style={{ background: '#FFF4CE', border: '1px solid #FFB900', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#7A6400', marginBottom: 8 }}>
              ⚠️ <strong>Vakantieperiode:</strong> {overlapping.map(vp => vp.naam).join(', ')} — goedkeuring is niet gegarandeerd vanwege bezetting.
            </div>
          );
        })()}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer', color: pForm.directGoedkeuren ? '#107C10' : '#605e5c' }}>
            <input type="checkbox" checked={pForm.directGoedkeuren} onChange={e => setPForm({...pForm, directGoedkeuren: e.target.checked})} />
            ✅ Direct goedkeuren
          </label>
          <button style={{ ...styles.ctaButton, padding: '8px 20px', fontSize: 13, opacity: (!pMwId || pSubmitting) ? 0.5 : 1, ...(pForm.directGoedkeuren ? { background: '#107C10' } : {}) }}
            disabled={!pMwId || pSubmitting || !pForm.Type || !pForm.StartDatum || !pForm.Uren}
            onClick={handlePortaalSubmit}>
            {pSubmitting ? 'Bezig...' : (pForm.directGoedkeuren ? `✅ Goedkeuren voor ${pMw?.Naam?.split(' ')[0] || '...'}` : `Indienen${pMw ? ` voor ${pMw.Naam.split(' ')[0]}` : ''}`)}
          </button>
        </div>
      </div>

      <div style={styles.twoCol} className="r-2col">
        <div style={styles.card} className="r-card">
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>Recente verlofaanvragen</h3>
            <button style={styles.linkButton} onClick={() => navigate('/verlof-overzicht')}>Alles bekijken →</button>
          </div>
          {recentAanvragen.length === 0 ? (
            <p style={styles.emptyText}>Geen verlofaanvragen gevonden.</p>
          ) : (
            <div style={styles.list}>
              {recentAanvragen.map(v => (
                <div key={v.Id} style={styles.listItem}>
                  <div style={styles.listItemLeft}>
                    <div style={styles.listItemTitle}>{v.MedewerkerNaam}</div>
                    <div style={styles.listItemSub}>
                      {(verlofTypes || []).find(t => t.key === v.Type)?.text || v.Type} · {fmtDatum(v.StartDatum)}{v.EindDatum && v.EindDatum !== v.StartDatum ? ` → ${fmtDatum(v.EindDatum)}` : ''} · {v.Uren}u
                    </div>
                  </div>
                  {v.Status === 'ingediend' && !v.isOpenZiek ? (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button style={{ width: 32, height: 32, borderRadius: 8, border: 'none', background: '#dff6dd', color: '#107C10', fontSize: 16, fontWeight: 700, cursor: 'pointer' }}
                        onClick={() => handleStatusUpdate(v.Id, 'goedgekeurd')} disabled={processingId === v.Id} title="Goedkeuren">✓</button>
                      <button style={{ width: 32, height: 32, borderRadius: 8, border: 'none', background: '#fde7e9', color: '#D13438', fontSize: 16, fontWeight: 700, cursor: 'pointer' }}
                        onClick={() => handleStatusUpdate(v.Id, 'afgekeurd')} disabled={processingId === v.Id} title="Afkeuren">✕</button>
                    </div>
                  ) : (
                    <StatusBadge status={v.Status} />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        <div style={styles.card} className="r-card">
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>Verlofsaldi medewerkers</h3>
            <button style={styles.linkButton} onClick={() => navigate('/admin')}>Beheer →</button>
          </div>
          <div style={styles.list}>
            {medewerkersExAdmin.filter(m => m.Rol !== 'flexkracht').slice(0, 8).map(m => {
              const totaal = m.BerekendeVerlofUren + m.MeegenomenUren;
              const resterend = totaal - m.VerlofOpgenomen;
              return (
                <div key={m.Id} style={styles.listItem}>
                  <div style={styles.listItemLeft}>
                    <div style={styles.listItemTitle}>{m.Naam}</div>
                    <div style={styles.listItemSub}>{m.ContractUren}u/w · {totaal}u totaal · {m.VerlofOpgenomen}u opgenomen</div>
                  </div>
                  <span style={{ fontWeight: 700, fontSize: 14, color: resterend >= 0 ? '#107C10' : '#D13438' }}>{resterend}u</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function Dashboard() {
  const navigate = useNavigate();
  const {
    currentUser, isAdmin, isManager, isFlexkracht, canManage,
    saldo, flexSaldo, verlofaanvragen, overuren, medewerkers, feestdagen, vakantiePerioden,
    flexPlanning, flexUren, verlofTypes, indienVerlofaanvraag, updateVerlofStatus,
  } = useApp();

  if (!currentUser) return null;

  // Flexkracht dashboard
  if (isFlexkracht) {
    const mijnPlanning = flexPlanning.filter(p => p.flexkrachtId === currentUser.id);
    const komendePlanning = mijnPlanning
      .filter(p => p.datum >= new Date().toISOString().split('T')[0])
      .sort((a, b) => a.datum.localeCompare(b.datum))
      .slice(0, 5);

    return (
      <div style={styles.container}>
        <div style={styles.welcomeCard} className="r-welcome">
          <div>
            <h2 style={styles.welcomeTitle}>Welkom, {currentUser.Naam?.split(' ')[0]} ⚡</h2>
            <p style={styles.welcomeText}>Je flexkracht-dashboard</p>
          </div>
          <button style={styles.ctaButton} onClick={() => navigate('/flex')}>Uren invoeren</button>
        </div>
        <div style={styles.statsGrid} className="r-stats">
          <StatCard icon="📅" label="Ingepland" value={`${flexSaldo?.ingepland || 0}u`} subtitle="dit jaar" color="#0078D4" />
          <StatCard icon="✅" label="Gewerkt" value={`${flexSaldo?.gewerkt || 0}u`} subtitle="dit jaar" color="#107C10" />
          <StatCard icon="💰" label="Uitbetaald" value={`${flexSaldo?.uitbetaald || 0}u`} subtitle="dit jaar" color="#8764B8" />
          <StatCard icon="⏳" label="Openstaand" value={`${flexSaldo?.openstaand || 0}u`} subtitle="te ontvangen" color="#E86E1C" />
        </div>
        <div style={styles.card} className="r-card">
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>Komende inplanningen</h3>
            <button style={styles.linkButton} onClick={() => navigate('/kalender')}>Kalender →</button>
          </div>
          {komendePlanning.length === 0 ? (
            <p style={styles.emptyText}>Geen komende inplanningen.</p>
          ) : (
            <div style={styles.list}>
              {komendePlanning.map(p => (
                <div key={p.id} style={styles.listItem}>
                  <div style={styles.listItemLeft}>
                    <div style={styles.listItemTitle}>{fmtDatum(p.datum)} · {p.startTijd} - {p.eindTijd}</div>
                    <div style={styles.listItemSub}>{p.uren}u — {p.omschrijving || 'Geen omschrijving'}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ===== ADMIN DASHBOARD — puur beheer (MUST be before isManager check!) =====
  if (isAdmin) {
    const openAanvragen = verlofaanvragen.filter(v => v.Status === 'ingediend');
    const medewerkersExAdmin = medewerkers.filter(m => (m.Status || 'actief') === 'actief');
    const recentAanvragen = verlofaanvragen
      .filter(v => v.medewerkerId !== currentUser.id) // admin's eigen test-aanvragen niet tonen
      .slice(0, 8);
    const recentOveruren = overuren
      .filter(o => o.medewerkerId !== currentUser.id)
      .slice(0, 5);

    return (
      <AdminDashboard
        navigate={navigate} currentUser={currentUser}
        openAanvragen={openAanvragen} medewerkersExAdmin={medewerkersExAdmin}
        recentAanvragen={recentAanvragen} verlofaanvragen={verlofaanvragen}
        overuren={overuren} medewerkers={medewerkers}
        verlofTypes={verlofTypes} indienVerlofaanvraag={indienVerlofaanvraag}
        feestdagen={feestdagen} updateVerlofStatus={updateVerlofStatus}
        vakantiePerioden={vakantiePerioden}
      />
    );
  }

  // Manager dashboard — can approve + see totals (AFTER isAdmin check)
  if (isManager && !isAdmin) {
    const medewerkersLijst = medewerkers.filter(m => (m.Status || 'actief') === 'actief');
    const openRequests = verlofaanvragen.filter(v => v.Status === 'ingediend' && !v.isOpenZiek);
    return (
      <div style={styles.container}>
        <div style={styles.welcomeCard} className="r-welcome">
          <div>
            <h2 style={styles.welcomeTitle}>Welkom, {currentUser.Naam?.split(' ')[0]} 📋</h2>
            <p style={styles.welcomeText}>
              {openRequests.length > 0
                ? `Er zijn ${openRequests.length} openstaande verlofaanvragen om te beoordelen.`
                : 'Alle aanvragen zijn afgehandeld.'}
            </p>
          </div>
          <button style={styles.ctaButton} onClick={() => navigate('/verlof-overzicht')}>Verlofoverzicht →</button>
        </div>
        <div style={styles.statsGrid} className="r-stats">
          <StatCard icon="📝" label="Openstaand verlof" value={openRequests.length} subtitle="te beoordelen" color="#FFB900" onClick={() => navigate('/verlof-overzicht')} />
          <StatCard icon="⏰" label="Open overuren" value={overuren.filter(o => o.Status === 'ingediend').length} subtitle="te beoordelen" color="#E86E1C" onClick={() => navigate('/overuren')} />
        </div>
        <div style={styles.card} className="r-card">
          <h3 style={styles.cardTitle}>Uurtotalen medewerkers</h3>
          <div className="r-table-wrap" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Medewerker</th>
                <th style={styles.th}>Contract</th>
                <th style={styles.th}>Meegen.</th>
                <th style={styles.th}>Totaal</th>
                <th style={styles.th}>Opgenomen</th>
                <th style={styles.th}>Resterend</th>
                <th style={styles.th}>Za. uren</th>
              </tr>
            </thead>
            <tbody>
              {medewerkersLijst.map(m => {
                const totaal = m.BerekendeVerlofUren + m.MeegenomenUren;
                const rest = totaal - m.VerlofOpgenomen;
                const zaDrempel = m.ZaterdagDrempel || 0;
                // Bereken zaterdaguren voor deze medewerker
                let zaInfo = null;
                if (zaDrempel > 0) {
                  const todayStr = new Date().toISOString().split('T')[0];
                  const mwOveruren = overuren.filter(o => o.medewerkerId === m.Id);
                  const zaGemaakt = mwOveruren.filter(o => o.Status === 'goedgekeurd' && (o.datum || '') <= todayStr && o.datum && new Date(o.datum + 'T12:00:00').getDay() === 6).reduce((s, o) => s + (o.uren || o.Uren || 0), 0);
                  const zaGepland = mwOveruren.filter(o => (o.Status === 'goedgekeurd' || o.Status === 'ingediend') && (o.datum || '') > todayStr && o.datum && new Date(o.datum + 'T12:00:00').getDay() === 6).reduce((s, o) => s + (o.uren || o.Uren || 0), 0);
                  const zaTotaal = zaGemaakt + zaGepland;
                  const alsOveruren = Math.max(0, zaGemaakt - zaDrempel);
                  const pctG = Math.min(100, (zaGemaakt / zaDrempel) * 100);
                  const pctP = Math.min(100 - pctG, (zaGepland / zaDrempel) * 100);
                  zaInfo = { gemaakt: zaGemaakt, gepland: zaGepland, totaal: zaTotaal, drempel: zaDrempel, bereikt: zaGemaakt >= zaDrempel, alsOveruren, pctG, pctP };
                }
                return (
                  <tr key={m.Id} style={styles.tr}>
                    <td style={styles.td}><strong>{m.Naam}</strong></td>
                    <td style={styles.td}>{m.BerekendeVerlofUren}u</td>
                    <td style={styles.td}>{m.MeegenomenUren}u</td>
                    <td style={styles.td}>{totaal}u</td>
                    <td style={styles.td}>{m.VerlofOpgenomen}u</td>
                    <td style={styles.td}><strong style={{ color: rest < 0 ? '#D13438' : '#107C10' }}>{rest}u</strong></td>
                    <td style={styles.td}>{zaInfo ? (
                      <div style={{ minWidth: 90 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: zaInfo.bereikt ? '#107C10' : '#8B6914', whiteSpace: 'nowrap' }}>
                          {zaInfo.totaal}/{zaInfo.drempel}u
                          {zaInfo.bereikt && <span style={{ color: '#107C10' }}> +{zaInfo.alsOveruren}u</span>}
                        </div>
                        <div style={{ background: '#edebe9', borderRadius: 2, height: 4, overflow: 'hidden', display: 'flex', marginTop: 3 }}>
                          {zaInfo.pctG > 0 && <div style={{ height: '100%', width: `${zaInfo.pctG}%`, background: '#107C10' }} />}
                          {zaInfo.pctP > 0 && <div style={{ height: '100%', width: `${zaInfo.pctP}%`, background: '#D13438' }} />}
                        </div>
                        <div style={{ fontSize: 9, color: '#a19f9d', marginTop: 2, whiteSpace: 'nowrap' }}>
                          {zaInfo.gemaakt}u ✓{zaInfo.gepland > 0 ? ` · ${zaInfo.gepland}u 🔮` : ''}
                        </div>
                      </div>
                    ) : <span style={{ color: '#c8c6c4' }}>—</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>{/* end r-table-wrap */}
        </div>
      </div>
    );
  }

  // ===== MEDEWERKER DASHBOARD — persoonlijk portaal =====
  const openAanvragen = verlofaanvragen.filter((v) => v.Status === 'ingediend');
  const mijnRecentAanvragen = verlofaanvragen.filter(v => v.medewerkerId === currentUser.id).slice(0, 5);
  const mijnRecentOveruren = overuren.filter(o => o.medewerkerId === currentUser.id).slice(0, 5);
  const mijnFlexBoekingen = verlofaanvragen.filter(v =>
    v.medewerkerId === currentUser.id &&
    (v.Type === 'flex-plus' || v.Type === 'flex')
  ).slice(0, 10);

  return (
    <div style={styles.container}>
      <div style={styles.welcomeCard} className="r-welcome">
        <div>
          <h2 style={styles.welcomeTitle}>Welkom, {currentUser.Naam?.split(' ')[0]} 👋</h2>
          <p style={styles.welcomeText}>
            {`Je hebt nog ${saldo?.resterend || 0} verlofuren over dit jaar.`}
          </p>
        </div>
        <button style={styles.ctaButton} onClick={() => navigate('/verlof-aanvragen')}>Verlof aanvragen</button>
      </div>
      <div style={styles.statsGrid} className="r-stats">
        {/* Saldo breakdown card */}
        <div style={{ ...styles.statCard, gridColumn: 'span 2', padding: '20px 24px' }}>
          <div style={{ width: '100%' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#1a1a2e', marginBottom: 12 }}>🏖️ Verlofsaldo</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', color: '#605e5c' }}>
                <span>Standaard verlofuren:</span><strong style={{ color: '#0078D4' }}>{saldo?.berekendeUren || 0}u</strong>
              </div>
              {(saldo?.meegenomenUren || 0) > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#8764B8' }}>
                  <span>📦 Meegenomen vorig jaar:</span><strong>+{saldo.meegenomenUren}u</strong>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #edebe9', paddingTop: 4, marginTop: 2, fontWeight: 600 }}>
                <span>Totaal beschikbaar:</span><span style={{ color: '#323130' }}>{saldo?.totaalUren || 0}u</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', color: '#605e5c' }}>
                <span>Opgenomen:</span><span>−{saldo?.opgenomenUren || 0}u</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 15, borderTop: '2px solid #edebe9', paddingTop: 6, marginTop: 4, color: (saldo?.resterend || 0) >= 0 ? '#107C10' : '#D13438' }}>
                <span>Resterend verlof:</span><span>{saldo?.resterend || 0}u</span>
              </div>
              {(saldo?.overurenSaldo || 0) !== 0 && (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: '#107C10', marginTop: 4 }}>
                    <span>⏰ Overuren saldo:</span><strong>+{saldo.overurenSaldo}u</strong>
                  </div>
                  {(saldo?.overurenUitbetaald || 0) > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', color: '#8764B8', fontSize: 12, paddingLeft: 16 }}>
                      <span>💰 Waarvan uitbetaald:</span><span>−{saldo.overurenUitbetaald}u</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800, fontSize: 16, borderTop: '2px solid #E86E1C', paddingTop: 6, marginTop: 4, color: ((saldo?.resterend || 0) + (saldo?.overurenSaldo || 0)) >= 0 ? '#E86E1C' : '#D13438' }}>
                    <span>Totaal beschikbaar:</span><span>{(saldo?.resterend || 0) + (saldo?.overurenSaldo || 0)}u</span>
                  </div>
                </>
              )}
              {(saldo?.overurenToekomstig || 0) > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#0078D4', marginTop: 6, fontSize: 12, background: '#f0f6ff', padding: '6px 10px', borderRadius: 6, border: '1px solid #c7e0f4' }}>
                  <span>🔮 Toekomstige overuren:</span><strong>+{saldo.overurenToekomstig}u</strong>
                  <span style={{ color: '#605e5c', fontWeight: 400 }}>(telt mee na werkdatum)</span>
                </div>
              )}
              {(saldo?.zaterdagDrempel || 0) > 0 && (() => {
                const drempel = saldo.zaterdagDrempel;
                const gemaakt = saldo.zaterdagUrenGemaakt || 0;
                const gepland = saldo.zaterdagUrenGepland || 0;
                const totaal = gemaakt + gepland;
                const pctGemaakt = Math.min(100, (gemaakt / drempel) * 100);
                const pctGepland = Math.min(100 - pctGemaakt, (gepland / drempel) * 100);
                const drempelBereikt = totaal >= drempel;
                const alsOveruren = Math.max(0, gemaakt - drempel);
                return (
                <div style={{ marginTop: 8, background: '#faf9f8', border: '1px solid #edebe9', borderRadius: 8, padding: '10px 12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                    <span>📅 Zaterdaguren (onbetaald)</span>
                    <span style={{ color: drempelBereikt ? '#107C10' : '#E86E1C' }}>
                      {totaal} / {drempel}u
                    </span>
                  </div>
                  <div style={{ background: '#edebe9', borderRadius: 4, height: 10, overflow: 'hidden', position: 'relative', display: 'flex' }}>
                    {pctGemaakt > 0 && <div style={{
                      height: '100%', width: `${pctGemaakt}%`, transition: 'width 0.3s',
                      background: 'linear-gradient(90deg, #107C10, #0B6A0B)',
                    }} />}
                    {pctGepland > 0 && <div style={{
                      height: '100%', width: `${pctGepland}%`, transition: 'width 0.3s',
                      background: 'linear-gradient(90deg, #D13438, #A4262C)',
                    }} />}
                  </div>
                  <div style={{ display: 'flex', gap: 12, fontSize: 11, color: '#605e5c', marginTop: 5, flexWrap: 'wrap' }}>
                    <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: '#107C10', marginRight: 4, verticalAlign: 'middle' }} />Gewerkt: <strong>{gemaakt}u</strong></span>
                    {gepland > 0 && <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: '#D13438', marginRight: 4, verticalAlign: 'middle' }} />Gepland: <strong>{gepland}u</strong></span>}
                    {!drempelBereikt && <span>· Nog <strong>{Math.max(0, drempel - totaal)}u</strong> te gaan</span>}
                    {alsOveruren > 0 && <span style={{ color: '#107C10' }}>· <strong>+{alsOveruren}u</strong> als overuren</span>}
                  </div>
                </div>
                );
              })()}
            </div>
          </div>
        </div>
        <StatCard icon="⏰" label="Overuren saldo" value={`${saldo?.overurenSaldo || 0}u`}
          subtitle={saldo?.overurenUitbetaald > 0 ? `${saldo.overurenUitbetaald}u uitbetaald · ${saldo.overurenGebruiktTotaal || 0}u ingezet` : saldo?.overurenGebruiktTotaal > 0 ? `${saldo.overurenGebruiktTotaal}u ingezet bij verlof` : 'goedgekeurde uren'}
          color="#107C10" onClick={() => navigate('/overuren')} />
        {(saldo?.zaterdagDrempel || 0) > 0 && (() => {
          const gemaakt = saldo.zaterdagUrenGemaakt || 0;
          const gepland = saldo.zaterdagUrenGepland || 0;
          const totaal = gemaakt + gepland;
          const drempel = saldo.zaterdagDrempel;
          const bereikt = gemaakt >= drempel;
          const alsOveruren = saldo.zaterdagAlsOveruren || 0;
          const pctGemaakt = Math.min(100, (gemaakt / drempel) * 100);
          const pctGepland = Math.min(100 - pctGemaakt, (gepland / drempel) * 100);
          return (
            <div style={{ ...styles.statCard, cursor: 'pointer', minWidth: 180 }} onClick={() => navigate('/overuren')}>
              <div style={{ ...styles.statIcon, background: '#E86E1C15', color: '#E86E1C' }}>📅</div>
              <div style={{ flex: 1 }}>
                <div style={styles.statValue} className="stat-val">{totaal} / {drempel}u</div>
                <div style={styles.statLabel}>Zaterdaguren</div>
                {/* Mini progress bar */}
                <div style={{ background: '#edebe9', borderRadius: 3, height: 6, overflow: 'hidden', display: 'flex', marginTop: 6 }}>
                  {pctGemaakt > 0 && <div style={{
                    height: '100%', width: `${pctGemaakt}%`,
                    background: 'linear-gradient(90deg, #107C10, #0B6A0B)',
                  }} />}
                  {pctGepland > 0 && <div style={{
                    height: '100%', width: `${pctGepland}%`,
                    background: 'linear-gradient(90deg, #D13438, #A4262C)',
                  }} />}
                </div>
                {/* Legend */}
                <div style={{ display: 'flex', gap: 8, marginTop: 4, fontSize: 10, color: '#605e5c', flexWrap: 'wrap' }}>
                  <span><span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: 1, background: '#107C10', marginRight: 3, verticalAlign: 'middle' }} />{gemaakt}u gewerkt</span>
                  {gepland > 0 && <span><span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: 1, background: '#D13438', marginRight: 3, verticalAlign: 'middle' }} />{gepland}u gepland</span>}
                </div>
                {bereikt && alsOveruren > 0 && (
                  <div style={{ fontSize: 10, color: '#107C10', fontWeight: 600, marginTop: 2 }}>✅ +{alsOveruren}u als overuren</div>
                )}
                {!bereikt && totaal < drempel && (
                  <div style={{ fontSize: 10, color: '#605e5c', marginTop: 2 }}>Nog {drempel - totaal}u te gaan</div>
                )}
              </div>
            </div>
          );
        })()}
        <StatCard icon="📝" label="Mijn aanvragen" value={mijnRecentAanvragen.length}
          subtitle="dit jaar" color="#FFB900"
          onClick={() => navigate('/verlof-overzicht')} />
        {saldo?.weekFlexUren > 0 && (
          <StatCard icon="⚡" label="Flex/ADV balans" value={`${saldo.flexBalans > 0 ? '+' : ''}${saldo.flexBalans || 0}u`}
            subtitle={`${saldo.weekFlexUren}u/week · max ${saldo.flexMinWeekMax}u/week min · maand: ${saldo.flexBalansDezeMaand > 0 ? '+' : ''}${saldo.flexBalansDezeMaand || 0}u`} color={saldo.flexBalans >= 0 ? '#00B7C3' : '#D13438'} />
        )}
      </div>
      <div style={styles.twoCol} className="r-2col">
        <div style={styles.card} className="r-card">
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>Recente verlofaanvragen</h3>
            <button style={styles.linkButton} onClick={() => navigate('/verlof-overzicht')}>Alles bekijken →</button>
          </div>
          {mijnRecentAanvragen.length === 0 ? (
            <p style={styles.emptyText}>Geen verlofaanvragen gevonden.</p>
          ) : (
            <div style={styles.list}>
              {mijnRecentAanvragen.map((v) => (
                <div key={v.Id} style={styles.listItem}>
                  <div style={styles.listItemLeft}>
                    <div style={styles.listItemTitle}>
                      {(verlofTypes || []).find((t) => t.key === v.Type)?.text || v.Type}
                    </div>
                    <div style={styles.listItemSub}>{fmtDatum(v.StartDatum)}{v.EindDatum && v.EindDatum !== v.StartDatum ? ` → ${fmtDatum(v.EindDatum)}` : ''} · {v.Uren}u</div>
                  </div>
                  <StatusBadge status={v.Status} />
                </div>
              ))}
            </div>
          )}
        </div>
        <div style={styles.card} className="r-card">
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>Recente overuren</h3>
            <button style={styles.linkButton} onClick={() => navigate('/overuren')}>Alles bekijken →</button>
          </div>
          {mijnRecentOveruren.length === 0 ? (
            <p style={styles.emptyText}>Geen overuren geregistreerd.</p>
          ) : (
            <div style={styles.list}>
              {mijnRecentOveruren.map((o) => (
                <div key={o.Id} style={styles.listItem}>
                  <div style={styles.listItemLeft}>
                    <div style={styles.listItemTitle}>{isAdmin ? o.MedewerkerNaam : o.Omschrijving}</div>
                    <div style={styles.listItemSub}>{fmtDatum(o.Datum)} · {o.Uren} uur</div>
                  </div>
                  <StatusBadge status={o.Status} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {/* Flex/ADV detail card - only if flex is enabled */}
      {saldo?.weekFlexUren > 0 && (
        <div style={styles.card} className="r-card">
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>🔀 Flex/ADV overzicht</h3>
          </div>
          <div style={{ padding: '0 24px 20px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginBottom: 16 }}>
              <div style={{ background: '#00B7C310', borderRadius: 10, padding: 12, textAlign: 'center', border: '1px solid #00B7C330' }}>
                <div style={{ fontSize: 10, color: '#605e5c', marginBottom: 2 }}>Budget per week</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#00B7C3' }}>{saldo.weekFlexUren}u</div>
              </div>
              <div style={{ background: '#107C1010', borderRadius: 10, padding: 12, textAlign: 'center', border: '1px solid #107C1030' }}>
                <div style={{ fontSize: 10, color: '#605e5c', marginBottom: 2 }}>Plus (jaar)</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#107C10' }}>+{saldo.flexPlusUren}u</div>
              </div>
              <div style={{ background: '#E86E1C10', borderRadius: 10, padding: 12, textAlign: 'center', border: '1px solid #E86E1C30' }}>
                <div style={{ fontSize: 10, color: '#605e5c', marginBottom: 2 }}>Min (jaar)</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#E86E1C' }}>−{saldo.flexMinUren}u</div>
              </div>
              <div style={{ background: saldo.flexBalans >= 0 ? '#107C1010' : '#D1343810', borderRadius: 10, padding: 12, textAlign: 'center', border: `1px solid ${saldo.flexBalans >= 0 ? '#107C1030' : '#D1343830'}` }}>
                <div style={{ fontSize: 10, color: '#605e5c', marginBottom: 2 }}>Jaarbalans</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: saldo.flexBalans >= 0 ? '#107C10' : '#D13438' }}>{saldo.flexBalans > 0 ? '+' : ''}{saldo.flexBalans}u</div>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
              <div style={{ background: '#F0F6FF', borderRadius: 8, padding: '10px 14px', border: '1px solid #c7e0f4', fontSize: 12 }}>
                <div style={{ fontWeight: 700, color: '#0078D4', marginBottom: 4 }}>Deze week</div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Min gebruikt:</span><span>{saldo.flexMinDezeWeek}u / {saldo.flexMinWeekMax}u</span></div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600, color: '#107C10' }}><span>Beschikbaar:</span><span>{saldo.flexMinWeekBeschikbaar}u</span></div>
              </div>
              <div style={{ background: '#F0F6FF', borderRadius: 8, padding: '10px 14px', border: '1px solid #c7e0f4', fontSize: 12 }}>
                <div style={{ fontWeight: 700, color: '#0078D4', marginBottom: 4 }}>Deze maand</div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Balans:</span><span style={{ color: saldo.flexBalansDezeMaand >= 0 ? '#107C10' : '#D13438', fontWeight: 600 }}>{saldo.flexBalansDezeMaand > 0 ? '+' : ''}{saldo.flexBalansDezeMaand}u</span></div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Min beschikb.:</span><span>{saldo.flexMinMaandBeschikbaar}u / {saldo.flexMinMaandMax}u</span></div>
              </div>
            </div>
            {mijnFlexBoekingen.length > 0 && (
              <>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#323130', marginBottom: 6 }}>Recente flex boekingen</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {mijnFlexBoekingen.map(v => (
                    <div key={v.Id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', background: v.Type === 'flex-plus' ? '#00B7C308' : '#E86E1C08', borderRadius: 6, border: `1px solid ${v.Type === 'flex-plus' ? '#00B7C320' : '#E86E1C20'}`, fontSize: 12 }}>
                      <div>
                        <span style={{ fontWeight: 600, color: v.Type === 'flex-plus' ? '#00B7C3' : '#E86E1C' }}>{v.Type === 'flex-plus' ? '➕ Plus' : '➖ Min'}</span>
                        <span style={{ color: '#605e5c', marginLeft: 8 }}>{fmtDatum(v.StartDatum)}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <strong>{v.Type === 'flex-plus' ? '+' : '−'}{v.Uren}u</strong>
                        <StatusBadge status={v.Status} />
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { display: 'flex', flexDirection: 'column', gap: 24 },
  welcomeCard: { background: 'linear-gradient(135deg, #1a1a2e 0%, #0f3460 100%)', borderRadius: 16, padding: '28px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#fff' },
  welcomeTitle: { fontSize: 24, fontWeight: 700, marginBottom: 6 },
  welcomeText: { fontSize: 14, opacity: 0.8 },
  ctaButton: { background: '#E86E1C', color: '#fff', border: 'none', borderRadius: 10, padding: '12px 24px', fontSize: 15, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 },
  statCard: { background: '#fff', borderRadius: 12, padding: '20px 24px', display: 'flex', alignItems: 'center', gap: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
  statIcon: { width: 48, height: 48, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, flexShrink: 0 },
  statValue: { fontSize: 28, fontWeight: 700, color: '#1a1a2e', lineHeight: 1 },
  statLabel: { fontSize: 13, color: '#605e5c', marginTop: 2 },
  statSubtitle: { fontSize: 11, color: '#a19f9d' },
  twoCol: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: 24 },
  card: { background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  cardTitle: { fontSize: 16, fontWeight: 600, color: '#1a1a2e' },
  linkButton: { background: 'none', border: 'none', color: '#E86E1C', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  list: { display: 'flex', flexDirection: 'column', gap: 8 },
  listItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', borderRadius: 8, background: '#faf9f8' },
  listItemLeft: { flex: 1, minWidth: 0 },
  listItemTitle: { fontSize: 14, fontWeight: 600, color: '#323130' },
  listItemSub: { fontSize: 12, color: '#a19f9d', marginTop: 2 },
  badge: { fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 20, whiteSpace: 'nowrap', flexShrink: 0, marginLeft: 12 },
  emptyText: { fontSize: 14, color: '#a19f9d', textAlign: 'center', padding: 24 },
  miniLabel: { fontSize: 11, fontWeight: 600, color: '#605e5c', marginBottom: 4, display: 'block' },
  miniInput: { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #d2d0ce', fontSize: 13, boxSizing: 'border-box' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '12px 16px', fontSize: 12, fontWeight: 600, color: '#605e5c', borderBottom: '2px solid #edebe9', textTransform: 'uppercase' },
  tr: { borderBottom: '1px solid #f3f2f1' },
  td: { padding: '12px 16px', fontSize: 14, color: '#323130' },
};

export default Dashboard;
